import { Box, Typography } from '@mui/material';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import palette from 'src/theme/palette';

import { formatCompactCurrency, formatRatio } from './formatters';
import type { RetentionCohortLtvCurvePoint } from './types';

/** Serie distinte: viola profondo vs rosa/corallo (evita indaco ≈ azzurro). */
const CHART_GROSS_MARGIN_LTV_COLOR = '#5b21b6';
const CHART_LTV_CAC_COLOR = '#e11d48';
const CHART_CUMULATIVE_LTV_COLOR = '#10b981';
const CHART_ORDERS_COLOR = '#f59e0b';

interface CohortLtvCurveChartProps {
  data: RetentionCohortLtvCurvePoint[];
}

type TooltipPayload = { payload?: RetentionCohortLtvCurvePoint };

function computeTightCurrencyDomain(
  data: RetentionCohortLtvCurvePoint[],
): [number, number] {
  const values: number[] = [];
  for (const row of data) {
    if (Number.isFinite(row.cumulativeLtv)) values.push(row.cumulativeLtv);
    if (Number.isFinite(row.cumulativeGrossMarginLtv))
      values.push(row.cumulativeGrossMarginLtv);
  }
  if (values.length === 0) return [0, 1];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  // Padding piccolo ma “visivo”: rende percepibili variazioni tipo 225€ → 274€ senza cambiare il grafico.
  const pad = Math.max(range * 0.08, max * 0.02);
  const lower = Math.max(0, min - pad);
  const upper = Math.max(lower + 1, max + pad);
  return [lower, upper];
}

function CohortLtvTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string | number;
}) {
  if (!active || !payload?.[0]?.payload) return null;
  const row = payload[0].payload;

  return (
    <Box
      sx={{
        bgcolor: palette.common.white,
        border: `1px solid ${palette.grey[200]}`,
        borderRadius: 1.5,
        px: 1.35,
        py: 1,
        boxShadow: '0 10px 28px -14px rgba(15, 23, 42, 0.35)',
        minWidth: 220,
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '0.875rem',
          color: palette.text.primary,
          lineHeight: 1.2,
        }}
      >
        By day {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.55,
          fontSize: '0.75rem',
          color: palette.text.secondary,
          lineHeight: 1.4,
        }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 800, color: CHART_CUMULATIVE_LTV_COLOR }}
        >
          {formatCompactCurrency(row.cumulativeLtv)}
        </Box>{' '}
        cumulative LTV
      </Typography>
      <Typography
        sx={{
          mt: 0.3,
          fontSize: '0.75rem',
          color: palette.text.secondary,
          lineHeight: 1.4,
        }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 700, color: CHART_GROSS_MARGIN_LTV_COLOR }}
        >
          {formatCompactCurrency(row.cumulativeGrossMarginLtv)}
        </Box>{' '}
        gross margin LTV
      </Typography>
      <Typography
        sx={{
          mt: 0.3,
          fontSize: '0.75rem',
          color: palette.text.secondary,
          lineHeight: 1.4,
        }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 800, color: CHART_ORDERS_COLOR }}
        >
          {row.ordersPerCustomer.toFixed(2)}
        </Box>{' '}
        orders per customer ·{' '}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {row.eligibleCustomers.toLocaleString()}
        </Box>{' '}
        eligible
      </Typography>
      {row.ltvToCacRatio != null && (
        <Typography
          sx={{
            mt: 0.3,
            fontSize: '0.75rem',
            color: palette.text.secondary,
            lineHeight: 1.4,
          }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 700, color: CHART_LTV_CAC_COLOR }}
          >
            {formatRatio(row.ltvToCacRatio)}
          </Box>{' '}
          LTV : CAC
        </Typography>
      )}
    </Box>
  );
}

export function CohortLtvCurveChart({ data }: CohortLtvCurveChartProps) {
  if (data.length === 0) {
    return (
      <Typography sx={{ color: palette.text.secondary, fontSize: '0.8125rem' }}>
        Not enough cohort data to plot LTV maturation yet.
      </Typography>
    );
  }

  const currencyDomain = computeTightCurrencyDomain(data);

  return (
    <Box
      className="retention-pdf-chart-fit"
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      <Box
        className="retention-pdf-chart-canvas"
        sx={{ width: '100%', height: 296, flexShrink: 0 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 8, right: 12, left: 2, bottom: 28 }}
          >
            <CartesianGrid vertical={false} stroke={palette.grey[200]} />
            <XAxis
              dataKey="day"
              tick={{ fill: palette.text.secondary, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              height={30}
            />
            <YAxis
              yAxisId="currency"
              domain={currencyDomain}
              tickFormatter={(value) => formatCompactCurrency(Number(value))}
              tick={{ fill: palette.text.secondary, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <YAxis
              yAxisId="ltvCac"
              orientation="left"
              tickFormatter={(v) =>
                v === null || Number.isNaN(Number(v))
                  ? ''
                  : `${Number(v).toFixed(2)}x`
              }
              tick={{ fill: CHART_LTV_CAC_COLOR, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={38}
            />
            <YAxis
              yAxisId="orders"
              orientation="right"
              tick={{ fill: palette.text.secondary, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={42}
            />
            <Tooltip
              content={<CohortLtvTooltip />}
              cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }}
            />
            <Legend
              verticalAlign="top"
              align="left"
              iconType="circle"
              wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
            />
            <Area
              yAxisId="currency"
              type="monotone"
              dataKey="cumulativeLtv"
              name="Cumulative LTV"
              stroke={CHART_CUMULATIVE_LTV_COLOR}
              fill="rgba(16, 185, 129, 0.18)"
              strokeWidth={2.5}
            />
            <Line
              yAxisId="currency"
              type="monotone"
              dataKey="cumulativeGrossMarginLtv"
              name="Gross margin LTV"
              stroke={CHART_GROSS_MARGIN_LTV_COLOR}
              strokeWidth={2.5}
              dot={{
                r: 3,
                fill: CHART_GROSS_MARGIN_LTV_COLOR,
                strokeWidth: 0,
              }}
            />
            <Line
              yAxisId="ltvCac"
              type="monotone"
              dataKey="ltvToCacRatio"
              name="LTV : CAC"
              stroke={CHART_LTV_CAC_COLOR}
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={{ r: 3, fill: CHART_LTV_CAC_COLOR, strokeWidth: 0 }}
              connectNulls
            />
            <Line
              yAxisId="orders"
              type="monotone"
              dataKey="ordersPerCustomer"
              name="Orders per customer"
              stroke={CHART_ORDERS_COLOR}
              strokeWidth={2}
              dot={{ r: 3, fill: CHART_ORDERS_COLOR, strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: palette.text.secondary,
          mt: 1.1,
          display: 'block',
          fontSize: '0.75rem',
          lineHeight: 1.45,
        }}
      >
        Read this as value maturation by cohort age: if 7-day ROAS looks weak
        but LTV keeps climbing by day 60 or 90, you have more room to scale with
        confidence. LTV : CAC uses the same blended CAC as the KPI tile (period
        Meta+Google spend ÷ customers analyzed).
      </Typography>
    </Box>
  );
}

export default CohortLtvCurveChart;
